<!-- eslint-disable vue/no-mutating-props -- `form` is the parent's useForm()
instance, passed down as shared mutable state so precognition validation and
submission stay owned by the page. Binding straight to its fields is the point. -->
<script lang="ts" setup>
import { ChatBubbleLeftIcon, PaperClipIcon, PhotoIcon, TrashIcon } from '@heroicons/vue/24/outline';
import { InputField } from '@vue-interface/input-field';
import { SelectField } from '@vue-interface/select-field';
import type { Agency, SubscriberList } from 'types';
import { computed, defineAsyncComponent, h, ref, useTemplateRef } from 'vue';
import axios from '../axios';
import { type PbtChannelType, pbtMessageLength, pbtMessageType } from '../composables/pbtMessageType';
import { route } from '../composables/routes';
import FormField from './FormField.vue';
import FormLayout from './FormLayout.vue';
import MobilePhone from './MobilePhone.vue';
import PbtMessageTypeBadge from './PbtMessageTypeBadge.vue';
import Skeleton from './Skeleton.vue';

export type MediaPathEntry = {
    fileName: string;
    extension?: string;
    fileSaveId?: string;
    mimetype: string;
    url: string;
};

export type PbtSendSegment = {
    id: number;
    name: string;
    total_subscribers: number | null;
};

type SendField = 'name' | 'message' | 'channel_type' | 'media_details' | 'pbt_segment_id';

// Structural shape of the precognition form we use. `validate`'s parameter is
// the field-name union so the concrete `useForm()` return assigns cleanly.
type SendForm = {
    name: string;
    message: string;
    channel_type: PbtChannelType;
    pbt_segment_id: number | null;
    media_details: MediaPathEntry[];
    processing: boolean;
    errors?: Partial<Record<string, string>>;
    validate(field: SendField): void;
    submit(): void;
};

const CHANNEL_TYPES = [pbtMessageType('SMS'), pbtMessageType('MMS')];

// Loaded on its own so the editor's ~600 KB of Tiptap and emoji data only
// downloads with this form, never with the pages that share its other parts.
const PbtMessageEditor = defineAsyncComponent({
    loader: () => import('./PbtMessageEditor.vue'),
    loadingComponent: () => h(Skeleton, { class: 'h-48 rounded-md lg:h-[21.5rem]' }),
});

const props = withDefaults(defineProps<{
    agency: Agency;
    list: SubscriberList;
    form: SendForm;
    buttonLabel: string;
    communicationCode?: string | null;
    segments?: PbtSendSegment[];
}>(), {
    communicationCode: null,
    segments: () => [],
});

const fileInput = useTemplateRef<HTMLInputElement>('fileInput');
const uploading = ref(0);
const uploadError = ref<string | null>(null);

const ACCEPTED_MIME = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'];
const MAX_BYTES = 950 * 1024;

function pickFiles() {
    fileInput.value?.click();
}

async function onFilesChosen(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';

    for(const file of files) {
        await uploadOne(file);
    }
}

async function uploadOne(file: File) {
    uploadError.value = null;

    if(!ACCEPTED_MIME.includes(file.type)) {
        uploadError.value = `${file.name}: only JPG, PNG, or GIF images and MP4 videos are supported.`;
        return;
    }

    if(file.size > MAX_BYTES) {
        uploadError.value = `${file.name}: file is over the 950 KB limit.`;
        return;
    }

    const body = new FormData();
    body.append('file', file);

    uploading.value += 1;

    try {
        const { data } = await axios.post<{ mediaPath: MediaPathEntry }>(
            route('api.pbt-media.upload', {
                agency: props.agency.slug,
                list: props.list.slug,
            }),
            body,
            // Override the global JSON default so axios + the browser send a real
            // multipart/form-data body with the file boundary.
            { headers: { 'Content-Type': 'multipart/form-data' } },
        );

        props.form.media_details.push(data.mediaPath);
    }
    catch (e: unknown) {
        uploadError.value = `${file.name}: upload failed.`;
        console.error('pbt media upload failed', e);
    }
    finally {
        uploading.value -= 1;
    }
}

function removeMedia(index: number) {
    props.form.media_details.splice(index, 1);
}

const messageType = computed(() => pbtMessageType(props.form.channel_type));
const messageLength = computed(() => pbtMessageLength(props.form.message));

/**
 * Switch between SMS and MMS. An SMS can't carry media, so going to SMS drops
 * any attachments — after asking, since they'd have to be uploaded again.
 */
function chooseChannel(channelType: PbtChannelType) {
    if(channelType === props.form.channel_type) {
        return;
    }

    if(!pbtMessageType(channelType).allowsMedia && props.form.media_details.length) {
        if(!confirm('An SMS can\'t include media. Switch to SMS and remove the attachments?')) {
            return;
        }

        props.form.media_details.splice(0);
    }

    props.form.channel_type = channelType;
    props.form.validate('channel_type');
}

function onSubmit() {
    props.form.submit();
}
</script>

<template>
    <!-- eslint-disable vue/no-mutating-props -- see the note above the script block -->
    <FormLayout
        :submit="onSubmit"
        :button-label="buttonLabel"
        :disabled="form.processing || uploading > 0"
        :activity="form.processing"
        button-row-class="mx-auto w-full max-w-6xl">
        <!-- Fields and preview stay centred; the rule and submit row run the
        full width so they line up with the rule under the page title. -->
        <div class="mx-auto flex w-full max-w-6xl flex-col gap-10 lg:flex-row lg:items-start lg:gap-12">
            <div class="min-w-0 flex-1">
                <FormField
                    compact-label
                    label="Internal Name"
                    description="Optional label to help find this broadcast later. Not sent to subscribers.">
                    <InputField
                        id="name"
                        v-model="form.name"
                        name="name"
                        :error="form.errors?.name"
                        @change="form.validate('name')" />
                </FormField>

                <FormField
                    compact-label
                    label="Message Type"
                    description="SMS keeps the message to a single text. MMS allows longer copy and media."
                    required>
                    <div
                        class="grid grid-cols-1 gap-3 sm:grid-cols-2"
                        role="radiogroup"
                        aria-label="Message type">
                        <label
                            v-for="type in CHANNEL_TYPES"
                            :key="type.name"
                            class="flex cursor-pointer flex-col gap-1 rounded-md border px-3 py-2 transition"
                            :class="form.channel_type === type.name
                                ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500 dark:bg-sky-900/30'
                                : 'border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'"
                            :data-channel-type="type.name">
                            <input
                                type="radio"
                                name="channel_type"
                                class="sr-only"
                                :value="type.name"
                                :checked="form.channel_type === type.name"
                                @change="chooseChannel(type.name)">
                            <span class="flex items-center gap-2 font-medium">
                                <PhotoIcon
                                    v-if="type.allowsMedia"
                                    class="size-4" />
                                <ChatBubbleLeftIcon
                                    v-else
                                    class="size-4" />
                                {{ type.name }}
                            </span>
                            <span class="text-xs text-neutral-500 dark:text-neutral-400">
                                Up to {{ type.maxLength }} characters{{ type.allowsMedia ? ', images or video' : ', text only' }}.
                                {{ type.credits }} {{ type.credits === 1 ? 'credit' : 'credits' }} per message.
                            </span>
                        </label>
                    </div>
                    <p
                        v-if="form.errors?.channel_type"
                        class="text-sm text-rose-500 mt-1">
                        {{ form.errors.channel_type }}
                    </p>
                </FormField>

                <FormField
                    compact-label
                    label="Message"
                    :description="messageType.allowsMedia
                        ? `Up to ${messageType.maxLength} characters.`
                        : `Up to ${messageType.maxLength} characters, so it goes out as a single text.`"
                    required>
                    <PbtMessageEditor
                        id="message"
                        v-model="form.message"
                        :agency="agency"
                        :list="list"
                        @blur="form.validate('message')" />
                    <p
                        v-if="form.errors?.message"
                        class="text-sm text-rose-500 mt-1">
                        {{ form.errors.message }}
                    </p>

                    <div class="mt-2 flex items-center justify-end gap-2 text-xs text-neutral-500 dark:text-neutral-400">
                        <PbtMessageTypeBadge
                            :channel-type="form.channel_type"
                            size="sm" />
                        <span
                            :class="{ 'font-semibold text-rose-500': messageLength > messageType.maxLength }"
                            data-character-count>
                            {{ messageLength }} / {{ messageType.maxLength }}
                        </span>
                    </div>
                </FormField>

                <FormField
                    v-if="messageType.allowsMedia"
                    compact-label
                    label="Media"
                    description="Attach JPG, PNG, or GIF images or MP4 videos (max 950 KB each).">
                    <div class="space-y-3">
                        <ul
                            v-if="form.media_details.length"
                            class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            <li
                                v-for="(entry, index) in form.media_details"
                                :key="entry.url"
                                class="relative rounded-md border border-neutral-200 dark:border-neutral-700 overflow-hidden bg-neutral-50 dark:bg-neutral-900">
                                <video
                                    v-if="entry.mimetype?.startsWith('video/')"
                                    :src="entry.url"
                                    class="block w-full h-24 object-cover bg-black"
                                    muted
                                    preload="metadata" />
                                <img
                                    v-else
                                    :src="entry.url"
                                    :alt="entry.fileName"
                                    class="block w-full h-24 object-cover">
                                <div class="px-2 py-1 text-xs truncate">
                                    {{ entry.fileName }}
                                </div>
                                <button
                                    type="button"
                                    class="absolute top-1 right-1 bg-white/90 dark:bg-neutral-800/90 hover:bg-rose-500 hover:text-white text-neutral-600 dark:text-neutral-300 rounded-full p-1 transition"
                                    :aria-label="`Remove ${entry.fileName}`"
                                    @click="removeMedia(index)">
                                    <TrashIcon class="size-4" />
                                </button>
                            </li>
                        </ul>

                        <div class="flex items-center gap-3">
                            <button
                                type="button"
                                class="btn btn-secondary inline-flex items-center gap-2"
                                :disabled="uploading > 0"
                                @click="pickFiles">
                                <PaperClipIcon class="size-4" />
                                <span>{{ uploading > 0 ? `Uploading… (${uploading})` : 'Attach Media' }}</span>
                            </button>
                            <input
                                ref="fileInput"
                                type="file"
                                accept="image/jpeg,image/png,image/gif,video/mp4"
                                multiple
                                class="hidden"
                                @change="onFilesChosen">
                        </div>

                        <p
                            v-if="uploadError"
                            class="text-sm text-rose-500">
                            {{ uploadError }}
                        </p>
                        <p
                            v-if="form.errors?.media_details"
                            class="text-sm text-rose-500">
                            {{ form.errors.media_details }}
                        </p>
                    </div>
                </FormField>

                <FormField
                    v-if="segments.length"
                    compact-label
                    label="Audience"
                    description="Send to everyone on this list, or narrow it to a segment.">
                    <SelectField
                        id="pbt_segment_id"
                        v-model="form.pbt_segment_id"
                        name="pbt_segment_id"
                        :error="form.errors?.pbt_segment_id"
                        @change="form.validate('pbt_segment_id')">
                        <option :value="null">
                            Everyone on this list
                        </option>
                        <option
                            v-for="segment in segments"
                            :key="segment.id"
                            :value="segment.id">
                            {{ segment.name }}{{ segment.total_subscribers === null ? '' : ` (${segment.total_subscribers})` }}
                        </option>
                    </SelectField>
                </FormField>
            </div>

            <!-- Deliberately not sticky: the phone is nearly as tall as the
            fields beside it, so sticky could only engage for the last ~50px of
            a ~270px scroll — the preview visibly detached from the form right
            at the end, which read as drag rather than as pinning. -->
            <div class="flex shrink-0 justify-center">
                <MobilePhone
                    :name="list.name"
                    :phone-number="communicationCode"
                    :message="form.message ?? ''"
                    :images="messageType.allowsMedia ? form.media_details : []" />
            </div>
        </div>

        <div class="mt-8 border-b border-neutral-200 dark:border-neutral-700" />
    </FormLayout>
</template>
